<template>
  <div
    class="
      tw-fixed tw-mx-auto tw-z-toast tw-p-4
      two-drawer-layout__top-offset--after-secondary-subheader
      tw-left-1/2 tw-transform tw--translate-x-1/2
      empty:tw-hidden
    "
  >
    <TransitionGroup name="toast-manager">
      <SfToast
        v-for="toast in effectiveToasts"
        :key="toast.id"
        :class="['tw-mb-3', toast.classes]"
        :auto-dismiss-delay="toast.autoDismissAfter"
        :size="toast.size"
        :title-tooltip="toast.title"
        :message-tooltip="toast.message"
        :variant="toast.variant"
        :no-message-ellipsis="toast.noMessageEllipsis"
        @dismiss="() => toastStore.dismissToast(toast.id)"
      >
        <template #title>
          {{ toast.title }}
        </template>
        <template v-if="toast.message" #message>
          <span
            v-dompurify-html="toast.message"
            @click="(event) => handleMessageClick(event, toast.id)"
          />
        </template>
        <template #action>
          <SfButtonDs
            v-for="action in toast?.actions ?? []"
            :key="action?.label || action?.icon"
            variant="secondary"
            size="sm"
            :icon-only="!!action?.icon"
            :icon-before="action?.icon"
            :aria-label="action?.icon ? action.label : undefined"
            @click="() => handleAction(toast.id, action)"
          >
            {{ action.label }}
          </SfButtonDs>
        </template>
      </SfToast>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import type { SilverfinEvents } from '@app/types'
import type { ToastPayloadAction } from '@app/shared/types/toast'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { MAX_VISIBLE_TOASTS } from '../constants'
import SfToast from '@app/shared/vue/components/sf-notification/sf-toast.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import { SfLog } from '@app/adapters/logger'

import { useToastStore } from '@app/shared/pinia/toast'
import { useMessageBusGlobalToasts } from '../composables/use-message-bus-global-toasts'

defineOptions({
  name: 'ToastManager',
})

const props =
  defineProps<{
    messageBusChannel: string
    messageBusLastId: number
  }>()

useMessageBusGlobalToasts(props.messageBusChannel, props.messageBusLastId)

const toastStore = useToastStore()
const { toastById, toastOrder } = storeToRefs(toastStore)

const effectiveToasts = computed(() =>
  toastOrder.value
    .map((id) => toastById.value[id])
    .splice(0, MAX_VISIBLE_TOASTS)
)

// Toast messages may carry HTML (rendered via v-dompurify-html) with a
// `<a data-toast-event="...">` link. On click we dispatch a cancelable
// CustomEvent on `document` whose `detail` is the link's other `data-*`
// attributes (as strings). If any listener calls `preventDefault()`,
// `dispatchEvent` returns `false` — we then dismiss the toast and skip
// the navigation. If no listener intercepts, the link's `href` runs as
// usual. Lets feature pages opt into in-place handling without coupling
// the toast manager to any specific feature.
function handleMessageClick(event: MouseEvent, toastId: string): void {
  const target = event.target
  if (!(target instanceof Element)) return

  const link = target.closest<HTMLAnchorElement>('a[data-toast-event]')
  if (!link) return

  const eventName = link.dataset.toastEvent
  if (!eventName) return

  const detail: Record<string, string> = {}
  for (const [key, value] of Object.entries(link.dataset)) {
    if (key === 'toastEvent') {
      continue
    }

    if (typeof value === 'string') {
      detail[key] = value
    }
  }

  const toastEvent = new CustomEvent(eventName, {
    detail,
    cancelable: true,
    bubbles: true,
  })

  const preventClick = !document.dispatchEvent(toastEvent)
  if (preventClick) {
    event.preventDefault()
    toastStore.dismissToast(toastId)
  }
}

function handleAction(id: string, action: ToastPayloadAction): void {
  toastStore.dismissToast(id)

  if (action.url) {
    window.Turbolinks.visit(action.url)
  } else if (action.event) {
    const eventKey = Object.keys(action.event)[0] as keyof SilverfinEvents
    const payload = action.event[
      eventKey
    ] as SilverfinEvents[keyof SilverfinEvents]

    window.eventHub.$emit(eventKey, payload)
  } else {
    SfLog.error('No action defined for toast action', {
      error: new Error('No action defined for toast action'),
      extra: { action, ...window.Silverfin.jsonParams },
    })
  }
}
</script>
<style lang="scss" scoped>
.toast-manager {
  &-move,
  &-enter-active,
  &-leave-active {
    transition: all 0.3s ease;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(-30px);
  }

  &-leave-active {
    position: absolute;
  }
}
</style>
